// A connection's EDITS (docs/claude/connections.md *Edits*): every change made to
// one connection, as data (`ConnEdit`), made by one function (`applyConnEdit`)
// that keeps each field's rule — a figure stored to the tenth of a mm, a field
// back at its default stored as absent, a bend's insert (a pipe's at the height
// of the run under it) and its removal (none left: no list), an end re-glued
// keeping its height, a run height back at its automatic one cleared, a label as
// typed, a pipe type copied into the drawing and the unused ones pruned. Pure (no
// DOM): the app makes the undo step around it; the card, the grips, the 3D and
// section handles, the menus and the electrical schematic give edits, and the
// tests make them.

import type {
  Connection,
  ConnectionType,
  ConnEnd,
  Drawing,
  LineStyle,
  PipeType,
  Vec2,
} from '../shared/types';
import {
  connGeom,
  reglueEnd,
  reverseConnection,
} from './connections';
import { heightOnPath } from './pipes';
import { unreachable } from '../shared/exhaustive';

type Which = 'from' | 'to';

/** The choices a new connection takes when nothing says otherwise — the last
 *  ones used (the Connect tool's typed options, a card's selects): its
 *  discipline (`typed`: typed for this line, so it beats a port's and a pipe
 *  type's), its routing, its pipe type ('' none). */
export interface ConnSticky {
  type: ConnectionType;
  route: 'orthogonal' | 'straight';
  pipe: string;
  typed: boolean;
}

/** A new connection's own: its id, its ends (an object's id, `''` a free end,
 *  and where on it), its bends, and the discipline of a port it starts or ends
 *  on. */
export interface ConnSpec {
  id: string;
  from: string;
  to: string;
  fromEnd?: ConnEnd;
  toEnd?: ConnEnd;
  bends?: readonly Vec2[];
  type?: ConnectionType;
}

/** Who lays a connection (absent = Stenal). */
export type ConnInstaller = NonNullable<Connection['installedBy']>;

/** One change to a connection. A bend is a waypoint (`i` its index in
 *  `waypoints`); a point (`pointZ`) is one of its control points: 0 its From end,
 *  the last its To end, a bend between. `null` puts a figure back to the one it
 *  takes by itself. */
export type ConnEdit =
  | { kind: 'bendAdd'; at: number; p: Vec2 & { z?: number } }
  | { kind: 'bendMove'; i: number; p: Vec2; z?: number }
  | { kind: 'bendRemove'; i: number }
  | { kind: 'discipline'; type: ConnectionType }
  | { kind: 'installedBy'; by: ConnInstaller }
  | { kind: 'label'; text: string }
  | { kind: 'length'; mm: number | null }
  | { kind: 'lines'; n: number }
  | { kind: 'pipe'; type: string | null; keepDiscipline?: boolean }
  | { kind: 'pipeSize'; inner?: number; wall?: number }
  | { kind: 'pointZ'; i: number; z: number | null }
  | { kind: 'reglue'; which: Which; id: string; end?: ConnEnd }
  | { kind: 'reverse' }
  | { kind: 'route'; route: 'orthogonal' | 'straight' }
  | { kind: 'runZ'; z: number | null }
  | { kind: 'style'; style: LineStyle }
  | { kind: 'tag'; t: number }
  | { kind: 'thickness'; px: number };

/** What an edit may read and write beyond the connection: the drawing's copies of
 *  the pipe types it uses, and what its run height and a pipe's heights are
 *  worked out from. */
export type ConnEditDrawing = Pick<Drawing, 'connections' | 'instances'> & Partial<Drawing>;

export interface ConnEditContext {
  drawing: ConnEditDrawing;
  /** The pipe types a connection may be made of (the catalogue's, then those the
   *  drawing carries); a `pipe` edit naming another is refused. */
  pipeTypes?: readonly PipeType[];
}

/** Why an edit is not made: nothing there to change (no such bend or point, a
 *  run height on a pipe, a figure that is no number), not a length, a pipe too
 *  thin, a pipe type nobody knows. */
export type ConnRefusal = 'none' | 'notLength' | 'tooSmall' | 'unknownType';

/** The defaults a field takes when absent: set back to one, it is deleted. */
export const CONN_DEFAULTS = { installedBy: 'stenal', route: 'straight', style: 'dashed', thickness: 1.5, labelT: 0.5 } as const;
/** How far along its route a tag may slide: just off the ends, clear of the
 *  objects there. */
export const CONN_TAG_MIN = 0.02;
export const CONN_TAG_MAX = 0.98;
/** A pipe's thinnest bore and wall (mm). */
export const PIPE_MIN_INNER = 0.1;
export const PIPE_MIN_WALL = 0;

const r1 = (v: number): number => Math.round(v * 10) / 10;
const finite = (v: number | null | undefined): boolean => v == null || Number.isFinite(v);
const asDrawing = (d: ConnEditDrawing): Drawing => d as Drawing;

/** The end a control point `i` is, or null for a bend. */
const endAt = (c: Connection, i: number): Which | null => (i === 0 ? 'from' : i === (c.waypoints?.length ?? 0) + 1 ? 'to' : null);

/** Whether `edit` cannot be made on `c`, and why; null when it can. */
export function connEditRefusal(ctx: ConnEditContext, c: Connection, edit: ConnEdit): ConnRefusal | null {
  const bends = c.waypoints?.length ?? 0;
  switch (edit.kind) {
    case 'bendAdd': return edit.at >= 0 && edit.at <= bends && finite(edit.p.x) && finite(edit.p.y) && finite(edit.p.z) ? null : 'none';
    case 'bendMove': return edit.i >= 0 && edit.i < bends && finite(edit.p.x) && finite(edit.p.y) && finite(edit.z) ? null : 'none';
    case 'bendRemove': return edit.i >= 0 && edit.i < bends ? null : 'none';
    case 'length': return edit.mm == null || (Number.isFinite(edit.mm) && edit.mm >= 0) ? null : 'notLength';
    case 'lines': case 'thickness': return Number.isFinite(edit.kind === 'lines' ? edit.n : edit.px) ? null : 'none';
    case 'pipe': return edit.type == null || ctx.pipeTypes?.some((t) => t.id === edit.type) ? null : 'unknownType';
    case 'pipeSize':
      if (!c.pipe) return 'none';
      if (edit.inner != null && !(edit.inner >= PIPE_MIN_INNER)) return 'tooSmall';
      if (edit.wall != null && !(edit.wall >= PIPE_MIN_WALL)) return 'tooSmall';
      return null;
    case 'pointZ': return edit.i >= 0 && edit.i <= bends + 1 && finite(edit.z) ? null : 'none';
    case 'runZ': return !c.pipe && finite(edit.z) ? null : 'none';
    case 'tag': return Number.isFinite(edit.t) ? null : 'none';
    case 'discipline': case 'installedBy': case 'label': case 'reglue': case 'reverse': case 'route': case 'style': return null;
    default: return unreachable(edit, 'connection edit');
  }
}

/** Make `edit` on `c` (nothing when it is refused); true when it was made. */
export function applyConnEdit(ctx: ConnEditContext, c: Connection, edit: ConnEdit): boolean {
  if (connEditRefusal(ctx, c, edit)) return false;
  const d = ctx.drawing;
  switch (edit.kind) {
    case 'bendAdd': {
      // A pipe's new bend sits at the height of the run under it, so adding one
      // never moves the pipe; a height given (seen in a section) is its own.
      const g = edit.p.z == null && c.pipe ? connGeom(asDrawing(d), c) : null;
      const z = edit.p.z ?? (g ? heightOnPath(g.path.pts, edit.p) : undefined);
      (c.waypoints ??= []).splice(edit.at, 0, z == null ? { x: r1(edit.p.x), y: r1(edit.p.y) } : { x: r1(edit.p.x), y: r1(edit.p.y), z: r1(z) });
      break;
    }
    case 'bendMove': {
      const w = c.waypoints![edit.i];
      w.x = r1(edit.p.x);
      w.y = r1(edit.p.y);
      if (edit.z != null) w.z = r1(edit.z);
      break;
    }
    case 'bendRemove':
      c.waypoints!.splice(edit.i, 1);
      if (!c.waypoints!.length) delete c.waypoints;
      break;
    case 'discipline': c.type = edit.type; break;
    case 'installedBy': setOrDefault(c, 'installedBy', edit.by); break;
    case 'label':
      // As typed, in every language (docs/claude/names.md).
      c.label = edit.text;
      delete c.labelI18n;
      break;
    case 'length':
      if (edit.mm == null) delete c.length; else c.length = Math.round(edit.mm);
      break;
    case 'lines': c.lines = Math.max(1, Math.round(edit.n)); break;
    case 'pipe': setPipe(ctx, c, edit.type, !!edit.keepDiscipline); break;
    case 'pipeSize':
      if (edit.inner != null) c.pipe!.inner = edit.inner;
      if (edit.wall != null) c.pipe!.wall = edit.wall;
      break;
    case 'pointZ': {
      const which = endAt(c, edit.i);
      if (!which) {
        const w = c.waypoints![edit.i - 1];
        if (edit.z == null) delete w.z; else w.z = r1(edit.z);
        break;
      }
      const end = which === 'from' ? c.fromEnd : c.toEnd;
      if (edit.z != null) {
        if (which === 'from') (c.fromEnd ??= {}).z = r1(edit.z); else (c.toEnd ??= {}).z = r1(edit.z);
      } else if (end) {
        delete end.z;
        if (!Object.keys(end).length) { if (which === 'from') delete c.fromEnd; else delete c.toEnd; }
      }
      break;
    }
    case 'reglue': reglueEnd(c, edit.which, edit.id, edit.end); break;
    case 'reverse': reverseConnection(c); break;
    case 'route': setOrDefault(c, 'route', edit.route); break;
    case 'runZ': {
      // Within half a mm of the height it takes by itself: none, so it follows
      // the walls and roofs again.
      const auto = connGeom(asDrawing(d), c)?.run?.auto;
      if (edit.z == null || (auto != null && Math.abs(edit.z - auto) < 0.5)) delete c.runZ;
      else c.runZ = r1(edit.z);
      break;
    }
    case 'style': setOrDefault(c, 'style', edit.style); break;
    case 'tag': setOrDefault(c, 'labelT', Math.max(CONN_TAG_MIN, Math.min(CONN_TAG_MAX, edit.t))); break;
    case 'thickness': setOrDefault(c, 'thickness', Math.max(0.5, edit.px)); break;
    default: return unreachable(edit, 'connection edit');
  }
  return true;
}

/** A field set, or deleted when it is set back to its default. */
function setOrDefault<K extends keyof typeof CONN_DEFAULTS>(c: Connection, k: K, v: NonNullable<Connection[K]>): void {
  if (v === CONN_DEFAULTS[k]) delete c[k]; else (c as Record<K, unknown>)[k] = v;
}

/** Make `c` of the pipe type `id` — its diameter and wall the type's, its
 *  discipline the type's unless `keepDiscipline` (one typed for this line), the
 *  type copied into the drawing so it opens the same anywhere — or of none (a
 *  plain line again). The drawing keeps a copy of only the types it uses. */
function setPipe(ctx: ConnEditContext, c: Connection, id: string | null, keepDiscipline: boolean): void {
  const d = ctx.drawing;
  const t = id ? ctx.pipeTypes?.find((x) => x.id === id) : undefined;
  if (!t) delete c.pipe;
  else {
    c.pipe = { type: t.id, inner: t.inner, wall: t.wall };
    if (!keepDiscipline) c.type = t.discipline;
    const copy: PipeType = structuredClone(t);
    delete copy.rowId;
    const list = (d.pipeTypes ??= []);
    const i = list.findIndex((x) => x.id === t.id);
    if (i >= 0) list[i] = copy; else list.push(copy);
  }
  const used = new Set([...d.connections, c].map((x) => x.pipe?.type).filter(Boolean));
  if (d.pipeTypes) {
    d.pipeTypes = d.pipeTypes.filter((x) => used.has(x.id));
    if (!d.pipeTypes.length) delete d.pipeTypes;
  }
}

/** A new connection (docs/claude/connections.md *A new connection*) — the ONE
 *  way one is born: one line, no label; its discipline the one typed for it,
 *  else a port's at an end, else the last used; its routing and its pipe the
 *  last used (a pipe's discipline its type's unless one was typed), through the
 *  same edits as any later change. Not added to the drawing: the caller does,
 *  with the undo step. */
export function newConnection(ctx: ConnEditContext, spec: ConnSpec, sticky: ConnSticky): Connection {
  const c: Connection = { id: spec.id, from: spec.from, to: spec.to, lines: 1, label: '', type: sticky.typed ? sticky.type : spec.type ?? sticky.type };
  if (spec.fromEnd) c.fromEnd = structuredClone(spec.fromEnd);
  if (spec.toEnd) c.toEnd = structuredClone(spec.toEnd);
  if (spec.bends?.length) c.waypoints = spec.bends.map((p) => ({ x: p.x, y: p.y }));
  applyConnEdit(ctx, c, { kind: 'route', route: sticky.route });
  if (sticky.pipe) applyConnEdit(ctx, c, { kind: 'pipe', type: sticky.pipe, keepDiscipline: sticky.typed });
  return c;
}

/** A pipe type saved in Tools ▸ Pipe types: the drawing's copy follows, and so
 *  does every pipe of it still at the old copy's diameter and wall (one changed
 *  by hand keeps its own). False when the drawing has no copy of it. */
export function followPipeType(d: ConnEditDrawing, t: PipeType): boolean {
  const list = d.pipeTypes;
  const i = list?.findIndex((x) => x.id === t.id) ?? -1;
  if (!list || i < 0) return false;
  const old = list[i];
  for (const c of d.connections) {
    if (c.pipe?.type !== t.id) continue;
    if (c.pipe.inner === old.inner && c.pipe.wall === old.wall) { c.pipe.inner = t.inner; c.pipe.wall = t.wall; }
  }
  const copy: PipeType = structuredClone(t);
  delete copy.rowId;
  list[i] = copy;
  return true;
}
